import { notify } from '../stores/notificationStore';

/**
 * Shared client-side persistence helper.
 *
 * Generalizes the GOOD pattern from chatStore.saveMessageToSupabase:
 *   1. Run the write.
 *   2. On transient failure, wait a short backoff and retry (once by default).
 *   3. On final failure, surface a STICKY, user-visible toast (so writes never
 *      fail silently) AND let the caller react via onFinalFailure.
 *
 * Stores import this as '../lib/persistence' so they stop swallowing write
 * errors. Reuses the existing notificationStore toast — no new UI is invented.
 *
 * SSR-safe and non-throwing: callers can `await` it and inspect { ok, error }
 * without try/catch.
 */

/** Supabase-style result shape: a truthy `error` means the write failed. */
type ErrorResult = { error: unknown };

function isErrorResult<T>(value: ErrorResult | T): value is ErrorResult {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    Boolean((value as ErrorResult).error)
  );
}

/** Extract the `error` field from a Supabase-style result, if present. */
function extractError<T>(value: ErrorResult | T): unknown {
  if (typeof value === 'object' && value !== null && 'error' in value) {
    return (value as ErrorResult).error;
  }
  return undefined;
}

const BACKOFF_MS = 1500;

export interface PersistOptions {
  /** Number of retries after the first attempt. Default 1 (= 2 total tries). */
  retries?: number;
  /** Invoked once after all attempts are exhausted, alongside the toast. */
  onFinalFailure?: (err: unknown) => void;
  /** Override the backoff delay between attempts (ms). Default 1500. */
  backoffMs?: number;
}

export interface PersistResult {
  ok: boolean;
  error?: unknown;
}

/**
 * Run a persistence `fn` with retry + a sticky failure toast.
 *
 * `fn` should perform a single write and return either the value it produced
 * or a Supabase-style `{ error }` object. If `fn` throws, or returns a result
 * whose `error` field is truthy, the attempt is treated as a failure.
 *
 * @param label  Human-readable name of what's being saved (used in the toast,
 *               e.g. "character", "token positions").
 * @param fn     The write to perform. Re-invoked on each retry.
 * @param opts   Optional retry count / final-failure callback / backoff.
 * @returns      `{ ok: true }` on success, or `{ ok: false, error }` on final
 *               failure. Never throws.
 */
export async function persistWithRetry<T>(
  label: string,
  fn: () => Promise<ErrorResult | T>,
  opts: PersistOptions = {},
): Promise<PersistResult> {
  const retries = Math.max(0, opts.retries ?? 1);
  const backoffMs = opts.backoffMs ?? BACKOFF_MS;
  const totalAttempts = retries + 1;

  let lastError: unknown;

  for (let attempt = 0; attempt < totalAttempts; attempt++) {
    if (attempt > 0) {
      // Backoff before retrying — smooths over transient network blips.
      await new Promise((resolve) => setTimeout(resolve, backoffMs));
    }

    try {
      const result = await fn();
      if (isErrorResult(result)) {
        lastError = extractError(result);
        console.warn(
          `[persist] Save of "${label}" returned error (attempt ${attempt + 1}/${totalAttempts}):`,
          lastError,
        );
        continue;
      }
      // Success.
      return { ok: true };
    } catch (err) {
      lastError = err;
      console.warn(
        `[persist] Save of "${label}" threw (attempt ${attempt + 1}/${totalAttempts}):`,
        err,
      );
    }
  }

  // All attempts exhausted — surface to the user and notify the caller.
  console.error(`[persist] Save of "${label}" failed after ${totalAttempts} attempt(s):`, lastError);
  notifyPersistFailure(label, lastError);

  try {
    opts.onFinalFailure?.(lastError);
  } catch (cbErr) {
    // A misbehaving callback must never break the persistence path.
    console.error(`[persist] onFinalFailure callback for "${label}" threw:`, cbErr);
  }

  return { ok: false, error: lastError };
}

/**
 * Surface a sticky, user-visible persistence-failure toast.
 *
 * Thin wrapper over the existing notificationStore toast for callers that run
 * their own retry loop but still want the standard label-aware failure UX.
 * SSR-safe and non-throwing.
 */
export function notifyPersistFailure(label: string, err: unknown): void {
  try {
    notify({
      level: 'error',
      message: `Couldn't save ${label} — changes may be lost.`,
      detail: 'Refreshing may discard recent changes. Check your connection.',
      ttl: 0, // sticky — user must dismiss
    });
  } catch (notifyErr) {
    // notify() itself failing (e.g. odd SSR context) must not bubble up.
    console.error(`[persist] Failed to surface failure toast for "${label}":`, notifyErr, err);
  }
}
